//
import { defineStore } from 'pinia';
import { getDefaultState } from './state';
import type {
  IAlertDetails,
  IGlobalError,
  IGlobalStoreAgent,
  IGlobalStoreAppAlert,
  IGlobalStoreNotification,
  IGlobalStoreTask,
} from './globalStore.model';
import * as getters from './getters';
import bff from '@/shared/api/bff';
import { COUNTRIES, LOCALES } from '@/shared/models/constants.models';
import type { IAuditLog, IBootstrap } from '@/shared/api/models.bff';
import type { IAppConfigFeature } from '@/shared/models/appConfig.model';
import { getAuthClient } from '@/plugins/auth';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { version } from '../../../package.json';
import content from '@/shared/api/content';
import type { IDocumentMetadata } from '@/shared/api/models.content';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useChildRiderStore } from '@/modules/childrider/store/childrider.store';
import { useDebtSolutionsStore } from '@/modules/debtsolutions/store/debtsolutions.store';
import { useHomeStore } from '@/modules/home/store/home.store';
import type { IPolicyStorePolicy } from '@/modules/policy/store/policyStore.model.ts';
import { useDimeguidanceStore } from '@/modules/dimeguidance/store/dimeguidance.store';

export const useGlobalStore = defineStore('global', {
  state: getDefaultState,
  getters,
  actions: {
    // update locale in state management
    changeLocale(_locale: string | undefined) {
      if (!_locale?.includes('_')) {
        switch (_locale?.toLowerCase()) {
          case 'en':
            this.locale = LOCALES.en_US;
            break;
          case 'es':
            this.locale = LOCALES.es_US;
            break;
          case 'fr':
            this.locale = LOCALES.fr_CA;
            break;
          default:
            this.locale = LOCALES.en_US;
        }
      } else {
        switch (_locale) {
          case LOCALES.en_US.valueOf():
            this.locale = LOCALES.en_US;
            break;
          case LOCALES.es_US.valueOf():
            this.locale = LOCALES.es_US;
            break;
          case LOCALES.en_CA.valueOf():
            this.locale = LOCALES.en_CA;
            break;
          case LOCALES.fr_CA.valueOf():
            this.locale = LOCALES.fr_CA;
            break;
          default:
            this.locale = LOCALES.en_US;
        }
      }
    },
    validateLanguagePreference() {
      const customerStore = useCustomerStore();
      let languagesPrefs: LOCALES[] = window.appConfig.languagePreferences;
      if (languagesPrefs.length === 0) {
        languagesPrefs = [LOCALES.en_US, LOCALES.en_CA];
      }

      if (customerStore.customerProfile.country === COUNTRIES.CANADA.valueOf()) {
        this.localeLanguages = languagesPrefs.filter((lang) => lang.indexOf('_CA') > 0);
        if (!this.locale.includes('_CA')) {
          this.changeLocale(LOCALES.en_CA);
        }
      } else {
        this.localeLanguages = languagesPrefs.filter((lang) => lang.indexOf('_US') > 0);
        if (!this.locale.includes('_US')) {
          this.changeLocale(LOCALES.en_US);
        }
      }
    },
    // update language preference in backend
    async updateLocale(language: LOCALES) {
      this.locale = language;
      if (language.includes('es')) {
        this.spanishBanner = true;
      } else {
        this.spanishBanner = false;
      }
      const swapDashWithUnderscore = language.replace('-', '_');
      await this.updateLanguagePreference(swapDashWithUnderscore);
    },
    addAlert(value: IGlobalStoreAppAlert) {
      const containAlert = this.alerts.find((a: IGlobalStoreAppAlert) => {
        const doesContatinAlert =
          a.type === value.type &&
          a.context === value.context &&
          ((a.details?.message &&
            value.details?.message &&
            a.details.message === value.details.message) ||
            (a.details?.key && value.details?.key && a.details.key === value.details.key));
        return doesContatinAlert;
      });
      if (!containAlert) {
        value.index = this.alerts.length;
        this.alerts.push(value);
      }
    },
    removeAlert(index: number) {
      this.alerts = this.alerts.filter((alert) => alert.index !== index);
    },
    removeAlerts(context: string | null) {
      if (context) {
        this.alerts = this.alerts.filter((alert) => alert.context !== context);
      } else {
        this.alerts = [];
      }
    },
    bootstrap: async function () {
      const customerStore = useCustomerStore();
      this.rootLoading = true;
      try {
        const response: IBootstrap = await bff.bootstrap();
        response.tasks.forEach((obj) => {
          obj.shownFlag = false;
        });
        this.tasks = response.tasks as IGlobalStoreTask[];
        this.initialized = true;

        // Second option (update state management)
        this.changeLocale(response.customerProfile.preferredLang);

        // Merging the DB features and Json config features
        const features: IAppConfigFeature[] = window.appConfig.features;
        const { featureFlags } = response;
        featureFlags.forEach((element) => {
          features.forEach((el, idx) => {
            if (element.feature === el.feature) {
              features.splice(idx, 1);
            }
          });
        });
        const featureList = featureFlags.concat(features);
        this.features = featureList as IAppConfigFeature[];
        customerStore.updateCustomerProfile(response.customerProfile);
      } catch (e: unknown) {
        const err = e as IAlertDetails;
        const details = {
          key: 'L_failed_to_bootstrap',
          correlationId: err.correlationId,
          traceId: err.traceId,
          detail: `${err.message} ${err.detail ?? ''}`,
        };
        this.addAlert({ type: 'error', details, context: 'app' });
      }
    },
    // TODO: look for actions.changeLocale and call this instead
    async updateLanguagePreference(locale: string) {
      try {
        const response = await bff.updateLanguagePreference(locale);
        if (response.status === 200) {
          this.changeLocale(locale);
        }
      } catch (error) {
        this.addAlert({ type: 'error', details: error as IAlertDetails, context: 'MyAccount' });
        throw new Error('Failed to update language preference', { cause: error });
      }
    },
    changeLocalePreLogin(locale: string) {
      this.changeLocale(locale);
    },
    async snoozeEcomm() {
      const p = await bff.snoozeEcomm().catch((error) => {
        this.addAlert({ type: 'error', details: error as IAlertDetails, context: 'Ecomm' });
        throw error;
      });
      return p;
    },
    async snoozeVerifyPhone() {
      await bff.snoozeVerifyPhone();
      window.sessionStorage.setItem('hideVerifyPhone', 'true');
      const tasksObj: IGlobalStoreTask[] = JSON.parse(JSON.stringify(this.tasks));
      const idx = tasksObj.findIndex((a) => a.name === 'VERIFY_PHONE' && a.shownFlag === false);
      if (idx !== -1) {
        tasksObj[idx].shownFlag = true;
      }
      this.tasks = tasksObj;
    },
    async ackPolicy(data: { policyNum: string; locale: string }) {
      try {
        const resp = await bff.ackPolicy(data);
        if (resp.data) {
          const tasksObj = JSON.parse(JSON.stringify(this.tasks));
          const idx = tasksObj.findIndex(
            (a: { name: string; policyNumbers: string[]; shownFlag: boolean }) =>
              a.name === 'POLICY_ACK' && a.policyNumbers[0] === data.policyNum && !a.shownFlag
          );
          if (idx !== -1) {
            tasksObj[idx].shownFlag = true;
          }
          this.tasks = tasksObj;
          return true;
        } else {
          return false;
        }
      } catch (_e) {
        return false;
      }
    },
    async goToLogin() {
      const authClient = getAuthClient();
      await authClient.logout(); // ? just to clear the session
      await authClient.login();
    },
    addNotification(value: {
      component: string;
      details: { title: string };
      context: string;
      index: number;
    }) {
      const containDuplicateNotification = this.notifications.find((notification) => {
        const duplicateNotification =
          notification.context === value.context &&
          notification.component === value.component &&
          notification.details.title === value.details.title;
        return duplicateNotification;
      });
      if (!containDuplicateNotification) {
        value.index = this.notifications.length;
        this.notifications.push(value);
      }
    },
    removeNotification(index: number) {
      this.notifications = this.notifications.filter(
        (notification) => notification.index !== index
      );
    },
    removeNotifications(context: string) {
      if (context) {
        this.notifications = this.notifications.filter(
          (notification) => notification.context !== context
        );
      } else {
        this.notifications = [];
      }
    },
    updateNotification(notificationObject: IGlobalStoreNotification) {
      this.notifications = this.notifications.filter(
        (notification) => notification.index !== notificationObject.index
      );
      this.notifications.push(notificationObject);
    },
    updateSpanishBanner(showSpanishBanner: boolean) {
      this.spanishBanner = showSpanishBanner;
    },
    async getAlerts() {
      try {
        const response = await bff.getAlerts();
        if (response.status === 200) {
          if (response.data) {
            this.alertsAndNotifications = [...response.data];
          } else {
            this.addAlert({
              type: 'error',
              details: response as IAlertDetails,
              context: 'AlertsAndNotifications',
            });
          }
        }
      } catch (error) {
        this.addAlert({
          type: 'error',
          details: error as IAlertDetails,
          context: 'AlertsAndNotifications',
        });
        throw new Error('Failed to get alerts', { cause: error });
      }
    },
    async dismissAlert(alertId: string) {
      try {
        const response = await bff.dismissAlert(alertId);
        if (response.status !== 200) {
          return false;
        }
        return true;
      } catch (error) {
        this.addAlert({
          type: 'error',
          details: error as IAlertDetails,
          context: 'AlertsAndNotifications',
        });
        return false;
      }
    },
    async completeAlert({ alertId, payload }: { alertId: string; payload: object }) {
      try {
        const response = await bff.completeAlert(alertId, payload);
        if (response.status !== 200) {
          throw new Error();
        }
        return true;
      } catch (error) {
        this.addAlert({
          type: 'error',
          details: error as IAlertDetails,
          context: 'AlertsAndNotifications',
        });
        throw new Error('Failed to complete alert', { cause: error });
      }
    },
    async updateTermsOfUse(agreementId: string) {
      try {
        const response = await bff.updateTermsOfUse(agreementId);
        if (response.status !== 200) {
          return false;
        }
        return true;
      } catch (_e) {
        return false;
      }
    },
    async getDocuments() {
      const policyStore = usePolicyStore();
      const policyIds: string[] = policyStore.policies
        .filter((p: IPolicyStorePolicy) => p.active || p.pending)
        .map((d: IPolicyStorePolicy) => d.data?.policyNum);
      return bff.getDocuments(policyIds).then((response) => {
        if (response.status === 200) {
          this.documents = { ...response.data };
        }
      });
    },
    async getAppInfo() {
      const resp = await bff
        .getAppInfo()
        .then((response) => {
          if (response.status === 200) {
            this.meta.isMaintenanceMode = response.data?.isMaintenanceMode;
            this.meta.recommendedAppVersion = { ...response.data?.appVersion };
            return Promise.resolve(response.data?.isMaintenanceMode);
          }
          return false;
        })
        .catch((error) => {
          this.addAlert({
            type: 'error',
            details: error as IAlertDetails,
            context: 'MaintenanceMode',
          });
          return false;
        });
      return resp;
    },
    // TODO: add unit test
    async postAuditLog(auditLog: IAuditLog) {
      const customerStore = useCustomerStore();
      const customerProfile = customerStore.customerProfile;
      const { sessionId, appVersion, deviceInfo } = this.userSessionMeta;
      let username;
      const authClient = getAuthClient();
      try {
        await authClient.getDecodedAccessToken().then((_accessToken: { sub?: string }) => {
          username = _accessToken.sub ?? customerProfile.email;
        });
        // eslint-disable-next-line no-empty
      } catch (_error) {}
      const l = {
        username,
        sessionId,
        deviceId: deviceInfo?.deviceId,
        metadata: {
          deviceName: deviceInfo?.deviceModel,
          osVersion: deviceInfo?.deviceOsVersion,
          platform: deviceInfo?.devicePlatform,
          appVersion,
        },
      };
      // Do not await. Log and forget
      bff.postAuditLog({ ...l, ...auditLog }).catch(() => {
        /* log errors, do nothin */
      });
      return true;
    },
    async getOnePolicyDocument(data: IDocumentMetadata) {
      try {
        // TODO: consider renaming since it returns urls for mobile
        const base64String = await content.streamDocument(data); // this has separate implementations for web and mobile
        this.setBase64String(base64String);
      } catch (e) {
        this.addError({ error: e, context: 'app' } as IGlobalError);
      }
    },
    async getAllPolicyDocuments(data: IDocumentMetadata) {
      try {
        // TODO: consider renaming since it returns urls for mobile
        const base64String = await content.streamDocument(data); // this has separate implementations for web and mobile
        this.setBase64String(base64String);
      } catch (e) {
        this.addError({ error: e, context: 'app' } as IGlobalError);
      }
    },
    setBase64String(base64String: string = '') {
      this.base64String = base64String;
    },
    async buildUserSessionMetaInfo() {
      let sessionId = '';
      let deviceInfo = {
        model: '',
        osVersion: '',
        platform: '',
      };
      let deviceId = '';
      try {
        // TODO: consider using `Promise.allSettled`
        await Promise.all([CapacitorFacade.getDeviceInfo(), CapacitorFacade.getDeviceId()]).then(
          ([_deviceInfo, _deviceId]) => {
            deviceInfo = _deviceInfo;
            deviceId = _deviceId.identifier;
          }
        );
        // eslint-disable-next-line no-empty
      } catch (_error) {}
      try {
        sessionId = Array.from(window.crypto.getRandomValues(new Uint32Array(2))).join('');
        // eslint-disable-next-line no-empty
      } catch (_error) {}
      const i = {
        sessionId,
        appVersion: version,
        deviceInfo: {
          deviceId,
          deviceModel: deviceInfo.model,
          deviceOsVersion: deviceInfo.osVersion,
          devicePlatform: deviceInfo.platform,
        },
      };
      this.userSessionMeta = { ...i };
    },
    addError(value: IGlobalError | null) {
      /* eslint-disable-next-line */
      // @ts-ignore
      const details = value?.error?.response ? value.error.response.data : (value?.error ?? {});
      if (!details?.key) {
        details.key = 'L_generic_error_message';
      }
      const alert = {
        type: 'error',
        details,
        context: value?.context,
        icon: value?.icon,
      } as IGlobalStoreAppAlert;
      this.addAlert(alert);
    },
    addAgents(agents: IGlobalStoreAgent[]) {
      agents.forEach((agent) => {
        // Add agent only if agent does not exist in the store.agents
        const contain = this.agents.find((a) => a.agentId === agent.agentId);
        if (!contain) {
          this.agents.push(agent);
        }
      });
    },
    resetStore() {
      const globalStore = useGlobalStore();
      const policyStore = usePolicyStore();
      const customerStore = useCustomerStore();
      const chilRiderStore = useChildRiderStore();
      const debtSolutionsStore = useDebtSolutionsStore();
      const homeStore = useHomeStore();

      globalStore.$reset();
      policyStore.$reset();
      customerStore.$reset();
      chilRiderStore.$reset();
      debtSolutionsStore.$reset();
      homeStore.$reset();

      const ds = useDimeguidanceStore();
      ds.resetState();
    },
    setShowGoPaperlessWrapper(value: boolean) {
      this.showGoPaperlessWrapper = value;
    },
    setGoPaperlessType(value: string) {
      this.goPaperlessType = value;
    },
    updateShowNavigationConfirmation(showFlag: boolean) {
      this.showNavigationConfirmation = showFlag;
    },
    updateMobileNumber(payload: object) {
      return bff.updateMobileNumber(payload);
    },
  },
});
